<template>
    <div id="track-order" class="row">
        <div class="col-xs-12">
            <template v-if="order.status != 'Cancelled'">
                <div class="tracking-title">
                    <h1 class="title-color">{{ status[order.status].label }} {{ isDelayed ? "(Delayed)" : '' }} </h1>
                    <p v-if="isDelayed" v-html="delayedData.message" />
                    <p v-else>{{ status[order.status].message }}</p>
                </div>
                <div class="tracking-status">
                    <div class="order-steps">
                        <div class="progress-bg">
                            <div class="step step-1 confirmed" :class="{'active': trackingStep.confirmed}">
                                <span>Order<br>Confirmed</span>
                            </div>
                            <div class="step step-2 processed" :class="{'active': trackingStep.processed}">
                                <span>Payment Processed</span>
                            </div>
                            <div class="step step-3 building" :class="{'active': trackingStep.building}">
                                <span>Building</span>
                            </div>
                            <div class="step step-4 shipped" :class="{'active': trackingStep.shipped}">
                                <span>Shipped</span>
                            </div>
                            <!-- <div class="step step-5 delivered" :class="{'active': trackingStep.delivered}">
                                <span>Delivered</span>
                            </div> -->
                        </div>
                    </div>
                    <div class="estimated-ship-date">
                        <p>Estimated Ship Date: {{ order.shipping_date | formateShipDate }} </p>
                    </div>
                </div>
            </template>
            <template v-else>
                <div class="tracking-title">
                    <h1 class="title-color">
                        Order Cancelled
                    </h1>
                </div>
                <div class="estimated-ship-date">
                    <p>Your order was cancelled on {{ order.shipping_date | formateCancelledShipDate }}. Contact us if you have any questions.</p>
                </div>
            </template>
            <div class="order-summary">
                <h2 class="title-color">Order Summary</h2>
                <ul>
                    <li>
                        {{ order.details.cases.name }} {{ order.details.cases.color ? `(${order.details.cases.color})` : '' }}
                        {{ order.details.cases.quantity > 1 ? `(${order.details.cases.quantity})`: '' }}
                        <span>CASE</span>
                    </li>
                    <li>
                        {{ order.details.motherboards.name }} {{ order.details.motherboards.color ? `(${order.details.motherboards.color})` : '' }}
                        {{ order.details.motherboards.quantity > 1 ? `(${order.details.motherboards.quantity})`: '' }}
                        <span>MOBO</span>
                    </li>
                    <li>
                        {{ order.details.cpus.name }} {{ order.details.cpus.color ? `(${order.details.cpus.color})` : '' }}
                        {{ order.details.cpus.quantity > 1 ? `(${order.details.cpus.quantity})`: '' }}
                        <span>CPU</span>
                    </li>
                    <li>
                        {{ order.details.coolers.name }} {{ order.details.coolers.color ? `(${order.details.coolers.color})` : '' }}
                        {{ order.details.coolers.quantity > 1 ? `(${order.details.coolers.quantity})`: '' }}
                        <span>CPU COOLER</span>
                    </li>
                    <li>
                        {{ order.details.gpus.name }} {{ order.details.gpus.color ? `(${order.details.gpus.color})` : '' }}
                        {{ order.details.gpus.quantity > 1 ? `(${order.details.gpus.quantity})`: '' }}
                        <span>GPU</span>
                    </li>
                    <li>
                        {{ order.details.ram.name }}
                        <small>(max speed)</small>
                        <b v-html="getRamSlotsFromText(order.details.ram.capacity, order.details.ram.quantity)" />
                        <span>RAM</span>
                    </li>

                    <li v-for="(disk, index) in order.details.ssd" :key="'ssd-' + index">
                        {{ disk.name }} {{ disk.capacity }} {{ disk.color ? `(${disk.color})` : '' }} {{ disk.quantity > 1 ? disk.quantity : '' }}
                        <span>SSD</span>
                    </li>
                    <li v-for="(disk, index) in order.details.hdd" :key="'hdd-' + index">
                        {{ disk.name }} {{ disk.capacity }} {{ disk.color ? `(${disk.color})` : '' }} {{ disk.quantity > 1 ? disk.quantity : '' }}
                        <span>HDD</span>
                    </li>
                    <li>
                        {{ order.details.psus.name }}
                        <span>PSU</span>
                    </li>
                    <li v-for="(fan, index) in order.details.fans" :key="'fans-' + index">
                        {{ fan.name }} {{ fan.color ? `(${fan.color})` : '' }} {{ fan.quantity > 1 ? fan.quantity : '' }}
                        <span>FAN</span>
                    </li>
                    <li v-for="(extra, index) in order.details.extras" :key="'extras-' + index">
                        {{ extra.name }} {{ extra.color ? `(${extra.color})` : '' }}
                        <span>EXTRA</span>
                    </li>
                    <li v-for="(peripheral, index) in order.details.peripherals" :key="'peripherals-' + index">
                        {{ peripheral.name }} {{ peripheral.color ? `(${peripheral.color})` : '' }}
                        <span>PERIPHERAL</span>
                    </li>
                    <li v-for="(monitor, index) in order.details.monitors" :key="'monitors-' + index">
                        {{ monitor.name }} {{ monitor.color ? `(${monitor.color})` : '' }} {{ monitor.quantity > 1 ? monitor.quantity : '' }}
                        <span>MONITOR</span>
                    </li>
                    <li>
                        {{ order.details.softwares.name }}
                        <span>SOFTWARE</span>
                    </li>
                </ul>
            </div>
        </div>
    </div>
</template>

<script>
import { getRamSlotsFromText } from "@u/store-helpers";

export default {
    name: "TrackingOrder",
    filters: {
        formateShipDate(date) {
            if (moment(date).isValid()) {
                return moment(date).format("MM/DD/YYYY");
            }

            return "N/A";
        },
        formateCancelledShipDate(date) {
            if (moment(date).isValid()) {
                return moment(date).format("MMMM D, YYYY");
            }

            return "N/A";
        }
    },
    props: {
        order: {
            type: Object,
            required: true
        }
    },
    data() {
        return {
            status: {
                "Confirmed": {
                    step: 1,
                    label: "Order Confirmed",
                    message: "Your order has been confirmed and will be processed soon to be build!",
                },
                "Processed": {
                    step: 2,
                    label: "Payment Processed",
                    message: "Parts are being picked and your build will begin soon.",
                },
                "Building": {
                    step: 3,
                    label: "Building",
                    message: "Parts are being put in and cables are being managed.",
                },
                "Shipped": {
                    step: 4,
                    label: "Shipped",
                    message: "Packed and shipped. We're on the way.",
                },
                // Temporary we only have until step 4, just in case any order get status as "Delivered" we're
                //  going to still displaying "Shipped"
                "Delivered": {
                    step: 5,
                    label: "Shipped",
                    message: "Packed and shipped. We're on the way.",
                }/* ,
                "Delivered": {
                    step: 5,
                    label: "Delivered",
                    message: "GLHF.",
                } */
            },
            isDelayed: !!this.order.delays.length,
            delayedData: _.last(this.order.delays)
        }
    },
    computed: {
        currentStep() {
            return  this.status[this.order.status];
        },
        trackingStep() {
            return {
                confirmed: this.currentStep.step >= 1,
                processed: this.currentStep.step >= 2,
                building: this.currentStep.step >= 3,
                shipped: this.currentStep.step >= 4,
                delivered: this.currentStep.step >= 5
            }
        }
    },
    methods: {
        getRamSlotsFromText
    }
}
</script>

<style lang="scss" scoped>
@import '~@s/_asset-mixers.scss';

.tracking-status {
    border: 1px solid rgb(74, 74, 74);
    border-radius: 2px;
    padding: 25px 30px 17px 25px;
}

.title-color {
    color: $orchid;
}

#track-order {
    .tracking-title {
        h1 {
            font-size: 24px;
            margin: 0;
            margin-bottom: 5px;
        }

        p {
            color: white;
            margin-bottom: 20px;
        }
    }

    .estimated-ship-date {
        color: white;

        p {
            margin: 12px 0 0 0;
        }
    }

    .order-steps {
        height: 60px;
        display: flex;

        .progress-bg {
            height: 4px;
            background-color: #4a4a4a;
            width: 100%;
            position: relative;
            display: flex;

            .step {
                position: relative;
                height: 100%;
                background-color: inherit;

                span {
                    position: absolute;
                    top: 20px;
                    font-weight: 700;
                    color: #4a4a4a;

                    @media (max-width: 768px) {
                        font-size: 12px;
                    }


                    @media only screen and (max-device-width : 480px) {
                        font-size: 10px;
                    }
                }

                &.active {
                    span {
                        color: $orchid !important;
                    }
                }

                &:before {
                    content: "";
                    position: absolute;
                    top: -8px;
                    width: 20px;
                    height: 20px;
                    background-color: inherit;
                    border-radius: 3px;
                }

                &.active,
                &.active:before {
                    background-color: $orchid !important;
                    z-index: 1;
                }
            }

            .step-1 {
                width: 15%;

                @media only screen and (max-device-width : 480px) {
                    width: 30%;
                }

                &:before {
                    left: 0;
                }
            }

            .step-2 {
                width: 30%;

                @media only screen and (max-device-width : 480px) {
                    width: 60%;
                }

                span {
                    left: calc(50% - 10px);
                }

                &:before {
                    left: 50%;
                    transform: translateX(-50%);
                }
            }

            .step-3 {
                width: 40%;

                @media only screen and (max-device-width : 480px) {
                    width: 45%;
                }

                span {
                    left: calc(35% - 10px);
                }

                &:before {
                    left: 35%;
                    transform: translateX(-50%);
                }
            }

            .step-4 {
                width: 40%;

                span {
                    right: 30px;
                }

                &:before {
                    left: 100%;
                    transform: translateX(-50%);
                }

                &:before,
                span {
                    right: 0;
                }
            }
        }
    }

    .order-summary {
        h2 {
            text-transform: uppercase;
            font-size: 16px;
        }

        ul {
            text-transform: uppercase;
            padding-left: 0;
            margin-bottom: 0;
            list-style: none;
            font-weight: 500;

            li {
                color: white;
                font-size: 16px;
                font-weight: 700;
                margin-bottom: 10px;

                span {
                    font-size: 12px;
                    font-weight: 100;
                    opacity: 0.6;
                }
            }
        }
    }
}
</style>
